"use client";

import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {isPresentationRecord, presentedFields, type PresentedField} from "@repo/ui/gailgpt/tool-presentation";
import {usePhoneSurveysFeatureEnabled} from "@/hooks/usePhoneSurveysFeatureEnabled";
import {fetchUseCasesBatch} from "@/utils/scripts/liveReflectionData";
import {CollectionReview, ReviewFields, type ToolDetailsProps} from "@/components/chat/ToolPresentationDetails";
import {DocumentsService} from "@/services/documents/client";
import type {UUID} from "@/types/common/ids";
import {useVerifyScriptVoice} from "@/hooks/useVerifyScriptVoice";

function ScriptVoice({field}: {field: PresentedField & {value: string}}) {
   const {t} = useTranslation("common");
   const {voice} = useVerifyScriptVoice(field.entityName ? undefined : field.value);
   const name = field.entityName || (voice?.id === field.value ? voice.name : undefined);
   return <ReviewFields compact fields={[{...field, format: "entity", entityName: name || t("unknown")}]} />;
}

function KnowledgeDocuments({field}: {field: PresentedField & {value: string[]}}) {
   const {t} = useTranslation("knowledge");
   const key = JSON.stringify([...new Set(field.value)].sort((a, b) => a < b ? -1 : a > b ? 1 : 0));
   const [result, setResult] = useState<{key: string; names: Map<string, string>}>();
   useEffect(() => {
      let active = true;
      const ids: string[] = JSON.parse(key);
      void Promise.allSettled(ids.map(id => DocumentsService.get(id as UUID))).then(results => {
         const names = new Map<string, string>();
         results.forEach((result, index) => {
            if (result.status === "fulfilled" && result.value?.fileName?.trim()) names.set(ids[index], result.value.fileName);
         });
         if (active) setResult({key, names});
      });
      return () => { active = false; };
   }, [key]);
   const names = result?.key === key ? result.names : undefined;
   return <CollectionReview field={{...field, value: field.value.map(id => names?.get(id) ?? t("manageTagsModal.genericName"))}} />;
}

function suppliedAssociationName(record: Record<string, unknown>) {
   return [record.title, record.name].find((value): value is string => typeof value === "string" && !!value.trim());
}

/** Resolve labels only; the associations in the approval request remain unchanged. */
function useAssociationNames(records: Record<string, unknown>[] | null) {
   const ids = [...new Set((records ?? []).filter(record => !suppliedAssociationName(record))
      .map(record => record.id).filter((id): id is string => typeof id === "string" && !!id))].sort((a, b) => a.localeCompare(b));
   const key = JSON.stringify(ids);
   const [result, setResult] = useState<{key: string; names: Map<string, string>}>();
   useEffect(() => {
      const requestedIds: string[] = JSON.parse(key);
      if (!requestedIds.length) return;
      let active = true;
      fetchUseCasesBatch(requestedIds, true).then(useCases => {
         if (active) setResult({key, names: new Map([...useCases].map(([id, useCase]) => [id, useCase.title]))});
      }).catch(() => {});
      return () => { active = false; };
   }, [key]);
   return result?.key === key ? result.names : undefined;
}

function UseCaseAssociations({field}: {field: PresentedField & {value: unknown[]}}) {
   const {t} = useTranslation("ask");
   const records = field.value.every(isPresentationRecord) ? field.value as Record<string, unknown>[] : null;
   const names = useAssociationNames(records);
   const associationName = (record: Record<string, unknown>) => {
      const supplied = suppliedAssociationName(record);
      if (supplied) return supplied;
      return typeof record.id === "string" ? names?.get(record.id) || record.id : t("toolApproval.execution.unavailableUseCase");
   };
   return <CollectionReview field={field} initiallyOpen={false}
      preview={records ? <>{records.slice(0, 2).map(associationName).join(" · ")}{records.length > 2 ? t("toolApproval.execution.moreItems", {count: records.length - 2}) : ""}</> : undefined}
      columnLabel={column => column === "id" ? t("toolApproval.execution.useCase") : undefined}
      renderCell={(record, column) => column === "id" ? associationName(record) : undefined} />;
}

function renderScriptField(field: PresentedField) {
   if (field.path.at(-1) === "voice" && typeof field.value === "string" && field.value)
      return <ScriptVoice key={field.value} field={{...field, value: field.value}} />;
   if (field.path.at(-1) === "useCaseAssociations" && Array.isArray(field.value) && field.value.length)
      return <UseCaseAssociations field={{...field, value: field.value}} />;
   if (field.path.at(-1) === "documentIds" && Array.isArray(field.value) && field.value.length && field.value.every((id): id is string => typeof id === "string"))
      return <KnowledgeDocuments field={{...field, value: field.value}} />;
   if (field.path.at(-1) === "faqs" && Array.isArray(field.value) && field.value.length)
      return <CollectionReview field={{...field, value: field.value.map(item => isPresentationRecord(item)
         ? Object.fromEntries(Object.entries(item).filter(([key]) => key !== "details")) : item)}} />;
}

const unconfigured = ({value}: PresentedField) => value == null || value === "" ||
   (Array.isArray(value) && !value.length) || (isPresentationRecord(value) && !Object.keys(value).length);

export function ScriptDetails({fields}: {fields: PresentedField[]}) {
   const {t} = useTranslation("ask");
   const phoneSurveysEnabled = usePhoneSurveysFeatureEnabled();
   const visibleFields = fields.filter(field => phoneSurveysEnabled ||
      !(field.path.join(".") === "giveSurvey" || field.path.join(".") === "body.giveSurvey"))
      .map(field => field.path.at(-1) === "direction" ? {...field, choices: [
         {value: "inbound", label: {text: t("workspace.scriptDraft.directionInbound")}},
         {value: "outbound", label: {text: t("workspace.scriptDraft.directionOutbound")}},
      ]} : field);
   const configured = visibleFields.filter(field => !unconfigured(field));
   const isContent = (field: PresentedField) => field.emphasis === "primary" || field.format === "multiline" ||
      ["name", "agentName", "direction", "greeting", "goodbye", "useCaseAssociations"].includes(field.path.at(-1) ?? "");
   const content = configured.filter(isContent);
   const settings = configured.filter(field => !isContent(field));
   return <div className="space-y-5">
      {!!content.length && <ReviewFields fields={content} renderField={renderScriptField} />}
      {!!settings.length && <section className="border-t border-neutral pt-4" aria-label={t("toolApproval.createScript.settings")}>
         <h4 className="mb-3 text-xs font-medium text-500">{t("toolApproval.createScript.settings")}</h4>
         <ReviewFields fields={settings} compact renderField={renderScriptField} />
      </section>}
   </div>;
}

/** Adapts a tool request to the same script details used by settled reviews. */
export default function ScriptToolDetails({presentation, parameters}: ToolDetailsProps) {
   const {t} = useTranslation("ask");
   const fields = presentedFields(parameters, presentation.fields);
   return <div className="px-5 pb-5 pt-4">
      <ScriptDetails fields={fields} />
      {!fields.length && <p className="text-sm text-500">{t("toolApproval.execution.noParameters")}</p>}
   </div>;
}
